'use client';

import {useState, useEffect, useContext, useRef} from 'react';
import Box from '@mui/material/Box';
import DataContext from '@/contexts/DataContext';
import { fetchExprData, fetchTooltip, extractTrackedParams, extractChangedParams, fetchAPIData } from '@/lib/dataHandlers/DataFetchers';
import { InfoTooltip } from '../typography/info-tooltip';
import Stack from '@mui/material/Stack';
import { textColors } from '@/styles/theme/colors';
import Typography from '@mui/material/Typography';
import { useComponentLogger } from '@/lib/component-logger';

/**
 * Displays a an image
 * @param {} param0 
 * @returns 
 */
export function Image({config}) {
    const logger = useComponentLogger("Image");
    const { currentProject, selectedViewParams, prelimParamCount, changedParams } = useContext(DataContext);
    // logger.debug('img object:', imageObject, selectedViewParams, prelimParamCount);
    const [imgSrc, setImgSrc] = useState(null);
    const [tooltip, setTooltip] = useState(null);
    const [title, setTitle] = useState(config.title || null);
    const [contentLoading, setContentLoading] = useState(true);
    const trackedParamValsRef = useRef({});
    const trackedParamNamesRef = useRef({}); // names of the params that this component depends on, extracted from its api expr

    // fetch and set content
    const fetchContent = async () => {
        if (config.dataSource === null || config.dataSource === undefined) return;

        const dataSource = config.dataSource;
        

        if (dataSource.type === "text") {
            const url = config.dataSource.text || null;
            if (url) await fetchImage(url);
        } else if (dataSource.type === "apiExpr") {
            const data = await fetchExprData(currentProject, selectedViewParams, dataSource.expr);
            logger.debug('img data', data.rows);

            if (data?.rows && data.rows.length > 0) {
                let apiConfig = data.rows[0];
                if (apiConfig.title) {
                    setTitle(apiConfig.title);
                }
                if (apiConfig.val) {
                    const url = apiConfig.val.val || apiConfig.val || null;
                    if (url) await fetchImage(url);
                }
                // if (config.color) {
                //     // logger.debug('text color', config.color);
                //     if (textColors[config.color]) {
                //         setTextColor(textColors[config.color]);
                //     } else {
                //         setTextColor('text.primary');
                //     }
                // }
            }
        }

        setContentLoading(false);
    }

    const fetchImage = async (imgURL) => {
        const data = await fetchAPIData(imgURL, '', false);
        logger.debug('img api data', data);
        //since the img is received as based64 in the res
        if (typeof data === 'string' && data?.startsWith('iVBOR')) {
            // I added this to handle the different schemes for images
            setImgSrc(`data:image/png;base64,${data}`);
        }
    }

    const processTooltip = async() => {
        let tooltipContent = await fetchTooltip(currentProject, selectedViewParams, config);
        if (tooltipContent) setTooltip(tooltipContent);
    }

    useEffect(() => {
        setContentLoading(true);
        let trackedParams = extractTrackedParams(config.dataSource);
        let params = {};
        for (const param of trackedParams) {
            if (selectedViewParams[param]) params[param] = selectedViewParams[param].value;
        }

        logger.debug('tracked params', params);
        trackedParamValsRef.current = params;
        trackedParamNamesRef.current = trackedParams;

        fetchContent().catch(() => {});
        processTooltip().catch(() => {});
    }, []);

    useEffect(() => {
        // logger.debug('new img url', imgSrc);
    }, [imgSrc]);

    useEffect(() => {
        let shouldReFetch = extractChangedParams(trackedParamNamesRef.current, changedParams, selectedViewParams, trackedParamValsRef.current);
        logger.debug('changed params', changedParams, trackedParamNamesRef.current, shouldReFetch);
        if (shouldReFetch.changed) {
            trackedParamValsRef.current = shouldReFetch.paramRef;
            fetchContent();
            processTooltip();
        }
        // make sure to clear the content if any of the tracked params were removed
        // common use case: when a parent param changes, it clears the child param
        for (const param of trackedParamNamesRef.current) { 
            if (!selectedViewParams[param]) {
                setImgSrc(null);
                return;
            }
        }
    }, [changedParams]);

    if (imgSrc === null && !config.title) return null;

    return imgSrc ?  (
        <Stack spacing={3} sx={{height: '100%', display: 'flex', alignItems: 'center', justifyContent:'center'}}>
            {config?.title ? 
                <Typography color="text.secondary" variant='h6' sx={{display: 'flex', alignItems: 'center'}}>
                    {title}
                    {tooltip ? <InfoTooltip content={tooltip} /> : <></>}
                </Typography> 
                : <></>
            } 
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                alignSelf='center'
            >
                <Box
                    component="img"
                    sx={{
                        // height: 233,
                        width: '100%',
                        height: '100%',
                        // maxHeight: { xs: 233, md: 167 },
                        // maxWidth: { xs: 250, md: 250 },
                    }}
                    alt={title || "Image"}
                    src={imgSrc}
                    // src={`data:image/png;base64,${testBlob}`}
                    // src='https://data.clistek.io/pub/demo/Vitalcare_Alliance_1.png'
                />

            </Box>
            
        </Stack>
    ) : <></>;
}